import React, { useState, useRef, useEffect, Suspense } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import Sidebar from './Sidebar';
import { PageLoader } from '@shared/components/PageLoader';
import '@shared/styles/layout.css';

const RootLayout: React.FC = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [sidebarExpanded, setSidebarExpanded] = useState(false);
  const location = useLocation();
  const prevPathRef = useRef(location.pathname);

  // Helper to check if a path should hide the sidebar
  const shouldHideSidebar = (pathname: string): boolean => {
    // Sandbox project editor (e.g., /sandbox/algorithms/uuid, /sandbox/fullstack/uuid)
    // Note: /sandbox mode selector should SHOW sidebar, only hide for actual project editors
    if (/^\/sandbox\/[^/]+\/[^/]+/.test(pathname)) {return true;}
    // Other full-screen views
    if (pathname.startsWith('/education/problems/')) {return true;}
    if (pathname.startsWith('/education/courses/builder')) {return true;}
    if (pathname.startsWith('/nova/')) {return true;}
    return false;
  };

  const hideSidebar = shouldHideSidebar(location.pathname);
  const prevHideSidebar = shouldHideSidebar(prevPathRef.current);

  // Only reset sidebar when coming from a page without sidebar
  useEffect(() => {
    if (prevHideSidebar && !hideSidebar) {
      setSidebarExpanded(false);
    }
    prevPathRef.current = location.pathname;
  }, [location.pathname, hideSidebar, prevHideSidebar]);

  const handleMobileMenuClose = (): void => {
    setMobileMenuOpen(false);
  };

  const handleSidebarExpandedChange = (expanded: boolean): void => {
    setSidebarExpanded(expanded);
  };

  return (
    <div className="layout">
      <a href="#main-content" className="skip-link">
        Skip to main content
      </a>
      
      {!hideSidebar && (
        <Sidebar 
          isMobileOpen={mobileMenuOpen}
          onMobileClose={handleMobileMenuClose}
          onExpandedChange={handleSidebarExpandedChange}
        />
      )}
      
      <main 
        id="main-content"
        className={`layout__main ${sidebarExpanded ? 'layout__main--expanded' : ''} ${hideSidebar ? 'layout__main--no-sidebar' : ''}`}
      >
        <div className="layout__content">
          <Suspense fallback={<PageLoader size="md" />}>
            <Outlet />
          </Suspense>
        </div>
      </main>
    </div>
  );
};

export default RootLayout;
